import { captureException } from '@sentry/core';

/**
 * 「離脱イベントの未送信ペイロード」を localStorage に退避/復元する。
 *
 * 離脱時の一発送信（pagehide/beacon）はモバイルの OS 強制 kill 等で落ちうるため、
 * 操作のたびに最新ペイロードを保存しておき、次回起動時に未送信分を救出するのに使う。
 * 計測手段（PostHog/GA4 等）には依存しない汎用部品。
 */

// localStorage 例外は握りつぶしつつ Sentry に送る（黙って消さない）。op で操作種別を識別。
const reportStorageError = (error: unknown, op: 'save' | 'load' | 'clear'): void => {
  captureException(error, { tags: { feature: 'pendingExitStorage', op } });
};

/** 操作のたびに最新ペイロードを保存する。書き込み失敗・SSR では何もしない。 */
export function savePendingExit<T>(storageKey: string, payload: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch (error) {
    // QuotaExceeded / プライベートモード等。機能は止めず Sentry で可視化する
    reportStorageError(error, 'save');
  }
}

/** 未送信ペイロードを取り出す。無い/壊れている場合は null。 */
export function loadPendingExit<T>(storageKey: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (error) {
    reportStorageError(error, 'load');
    clearPendingExit(storageKey);
    return null;
  }
}

/** 送信成功後に退避データを削除する。 */
export function clearPendingExit(storageKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    reportStorageError(error, 'clear');
  }
}
